import { useDashboardFacilityTypePerformance } from '../../hooks/useDashboard';

interface Props {
  filters: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    geoLocationId?: string;
  };
}

// API returns rating as: "EXCELLENT" | "GOOD" | "FAIR" | "POOR" | "CRITICAL"
function ratingBadge(rating: string): { label: string; bg: string; color: string; barColor: string } {
  switch (rating?.toUpperCase()) {
    case 'EXCELLENT': return { label: 'Excellent', bg: 'var(--nhip-green-light)', color: 'var(--nhip-green)',  barColor: 'var(--nhip-green)' };
    case 'GOOD':      return { label: 'Good',      bg: '#EFF6FF',                color: '#1D4ED8',            barColor: '#3B82F6' };
    case 'FAIR':      return { label: 'Fair',       bg: '#FEF9C3',               color: '#92400E',            barColor: '#F59E0B' };
    case 'POOR':      return { label: 'Poor',       bg: '#FEF2F2',               color: 'var(--nhip-red)',    barColor: 'var(--nhip-red)' };
    default:          return { label: 'Critical',   bg: '#FEE2E2',               color: '#7F1D1D',            barColor: '#7F1D1D' };
  }
}

function SkeletonRow() {
  return (
    <tr className='border-b' style={{ borderColor: 'var(--nhip-border)' }}>
      {[140, 60, 80, 80].map((w, i) => (
        <td key={i} className='px-4 py-3'>
          <div className='h-3 rounded animate-pulse' style={{ width: w, backgroundColor: 'var(--nhip-border)' }} />
        </td>
      ))}
    </tr>
  );
}

export default function FacilityTypePerformanceTable({ filters }: Props) {
  const { data, isLoading } = useDashboardFacilityTypePerformance(filters);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: any[] = raw?.items ?? [];

  return (
    <div
      className='bg-white rounded-xl overflow-hidden'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      <div className='px-4 py-3 border-b flex items-center justify-between' style={{ borderColor: 'var(--nhip-border)' }}>
        <h2 className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>
          Facility Type Performance
        </h2>
        {raw?.period && (
          <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>{raw.period}</span>
        )}
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b' style={{ borderColor: 'var(--nhip-border)' }}>
              {['Type', 'Assess.', 'Score', 'Rating'].map(col => (
                <th
                  key={col}
                  className='px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider'
                  style={{ color: 'var(--nhip-ink-muted)' }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className='py-8 text-center text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>
                  No data for this period
                </td>
              </tr>
            ) : (
              rows.map((row: any) => {
                const badge = ratingBadge(row.rating);
                return (
                  <tr
                    key={row.facilityTypeId}
                    className='border-b last:border-0'
                    style={{ borderColor: 'var(--nhip-border)' }}
                  >
                    <td className='px-4 py-3'>
                      <div className='flex flex-col gap-1'>
                        <span className='text-sm font-medium' style={{ color: 'var(--nhip-ink)' }}>{row.facilityTypeName}</span>
                        {row.totalAssessments > 0 && (
                          <div className='h-1.5 rounded-full overflow-hidden' style={{ backgroundColor: 'var(--nhip-border)', width: 100 }}>
                            <div
                              className='h-full rounded-full'
                              style={{ width: `${Math.min(100, Number(row.averageScore))}%`, backgroundColor: badge.barColor }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className='px-4 py-3' style={{ color: 'var(--nhip-ink-muted)' }}>
                      {row.totalAssessments.toLocaleString()}
                    </td>
                    <td className='px-4 py-3 font-semibold' style={{ color: 'var(--nhip-ink)' }}>
                      {row.totalAssessments === 0 ? '—' : `${Number(row.averageScore).toFixed(1)}%`}
                    </td>
                    <td className='px-4 py-3'>
                      <span
                        className='inline-block px-2 py-0.5 rounded-md text-xs font-semibold'
                        style={{ backgroundColor: badge.bg, color: badge.color }}
                      >
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
